import { useState, useEffect } from "react";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=Inter:wght@400;500;600;700&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --ink: #0D1B2A;
    --ink-mid: #1a2e45;
    --parchment: #F5ECD7;
    --amber: #E8A045;
    --amber-light: #f7c97a;
    --sage: #4A7C6B;
    --sage-light: #6aad98;
    --off-white: #FAFAF7;
    --muted: #8a9ab0;
    --red-accent: #c84b4b;
  }

  .id-page {
    font-family: 'Inter', sans-serif;
    background: var(--off-white);
    color: var(--ink);
    min-height: 100vh;
    overflow-x: hidden;
  }

  /* ── HERO ── */
  .hero {
    background: var(--ink);
    position: relative;
    padding: 0;
    overflow: hidden;
  }

  .hero-bg-lines {
    position: absolute;
    inset: 0;
    background-image:
      repeating-linear-gradient(
        90deg,
        transparent,
        transparent 79px,
        rgba(255,255,255,.03) 79px,
        rgba(255,255,255,.03) 80px
      );
    pointer-events: none;
  }

  .hero-inner {
    position: relative;
    z-index: 2;
    display: grid;
    grid-template-columns: 1fr 420px;
    align-items: center;
    gap: 60px;
    max-width: 1200px;
    margin: 0 auto;
    padding: 90px 60px 110px;
  }

  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: rgba(232,160,69,.12);
    border: 1px solid rgba(232,160,69,.3);
    color: var(--amber-light);
    padding: 8px 18px;
    border-radius: 999px;
    font-size: .8rem;
    font-weight: 600;
    letter-spacing: .08em;
    text-transform: uppercase;
    margin-bottom: 28px;
  }

  .eyebrow-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--amber);
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: .5; transform: scale(.7); }
  }

  .hero-headline {
    font-family: 'Playfair Display', serif;
    font-size: 3.6rem;
    font-weight: 800;
    line-height: 1.08;
    color: #fff;
    margin-bottom: 22px;
  }

  .hero-headline em {
    font-style: normal;
    color: var(--amber);
  }

  .hero-sub {
    color: var(--muted);
    font-size: 1.05rem;
    line-height: 1.7;
    max-width: 480px;
    margin-bottom: 40px;
  }

  .hero-ctas {
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
  }

  .cta-primary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: var(--amber);
    color: var(--ink);
    padding: 15px 32px;
    border-radius: 10px;
    font-weight: 700;
    font-size: .95rem;
    text-decoration: none;
    transition: transform .2s, box-shadow .2s;
    box-shadow: 0 4px 20px rgba(232,160,69,.4);
  }

  .cta-primary:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 28px rgba(232,160,69,.5);
  }

  .cta-secondary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    color: #fff;
    padding: 15px 32px;
    border-radius: 10px;
    font-weight: 600;
    font-size: .95rem;
    text-decoration: none;
    border: 1.5px solid rgba(255,255,255,.25);
    transition: background .2s, border-color .2s;
  }

  .cta-secondary:hover {
    background: rgba(255,255,255,.08);
    border-color: rgba(255,255,255,.5);
  }

  /* ID Card Mockup */
  .card-stage {
    perspective: 900px;
  }

  .id-card {
    width: 340px;
    border-radius: 18px;
    overflow: hidden;
    box-shadow:
      0 2px 4px rgba(0,0,0,.1),
      0 12px 40px rgba(0,0,0,.45),
      0 40px 80px rgba(0,0,0,.3);
    transform: rotateY(-8deg) rotateX(4deg);
    transition: transform .4s;
    background: #fff;
  }

  .id-card:hover {
    transform: rotateY(0deg) rotateX(0deg);
  }

  .card-top-stripe {
    height: 8px;
    background: linear-gradient(90deg, var(--sage), var(--amber));
  }

  .card-header-bar {
    background: var(--ink);
    padding: 16px 22px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .card-uni-name {
    color: #fff;
    font-size: .78rem;
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;
    font-family: 'Inter', sans-serif;
  }

  .card-logo-circle {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: var(--amber);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
  }

  .card-body {
    padding: 22px;
    display: flex;
    gap: 18px;
    align-items: flex-start;
  }

  .card-photo {
    width: 80px;
    height: 96px;
    border-radius: 8px;
    background: linear-gradient(145deg, #e8e8e8, #d0d0d0);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 36px;
    flex-shrink: 0;
    border: 2px solid #eee;
  }

  .card-details {
    flex: 1;
  }

  .card-label {
    font-size: .63rem;
    font-weight: 700;
    letter-spacing: .1em;
    text-transform: uppercase;
    color: var(--muted);
    margin-bottom: 2px;
    margin-top: 10px;
  }

  .card-label:first-child { margin-top: 0; }

  .card-value {
    font-size: .88rem;
    font-weight: 600;
    color: var(--ink);
  }

  .card-type-badge {
    display: inline-block;
    background: var(--sage);
    color: #fff;
    font-size: .65rem;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 999px;
    margin-top: 3px;
  }

  .card-footer-bar {
    background: #f4f4f4;
    padding: 12px 22px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .card-barcode {
    display: flex;
    gap: 2px;
    align-items: center;
  }

  .card-barcode span {
    display: inline-block;
    background: var(--ink);
    height: 28px;
    border-radius: 1px;
  }

  .card-valid {
    font-size: .7rem;
    color: var(--muted);
    font-weight: 500;
  }

  /* Angle divider */
  .hero-angle {
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 70px;
    overflow: hidden;
  }

  .hero-angle svg {
    display: block;
    width: 100%;
    height: 100%;
  }

  /* ── STATS BAR ── */
  .stats-bar {
    background: var(--ink-mid);
    padding: 28px 60px;
  }

  .stats-inner {
    max-width: 1200px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0;
  }

  .stat-item {
    text-align: center;
    padding: 0 20px;
    border-right: 1px solid rgba(255,255,255,.08);
  }

  .stat-item:last-child { border-right: none; }

  .stat-num {
    font-family: 'Playfair Display', serif;
    font-size: 2.2rem;
    font-weight: 700;
    color: var(--amber);
  }

  .stat-desc {
    font-size: .8rem;
    color: var(--muted);
    margin-top: 3px;
    font-weight: 500;
  }

  /* ── MAIN CONTENT ── */
  .main-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 70px 60px;
  }

  .section-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: .75rem;
    font-weight: 700;
    letter-spacing: .12em;
    text-transform: uppercase;
    color: var(--sage);
    margin-bottom: 12px;
  }

  .section-label::before {
    content: '';
    display: block;
    width: 24px;
    height: 2px;
    background: var(--sage);
    border-radius: 1px;
  }

  .section-title {
    font-family: 'Playfair Display', serif;
    font-size: 2.2rem;
    font-weight: 700;
    color: var(--ink);
    margin-bottom: 40px;
  }

  /* Pricing */
  .pricing-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-bottom: 70px;
  }

  .price-card {
    background: #fff;
    border-radius: 20px;
    padding: 40px;
    position: relative;
    overflow: hidden;
    box-shadow: 0 4px 24px rgba(0,0,0,.06);
    transition: transform .25s, box-shadow .25s;
  }

  .price-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 40px rgba(0,0,0,.1);
  }

  .price-card-accent {
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 5px;
  }

  .price-card.free .price-card-accent { background: linear-gradient(90deg, #22c55e, #4ade80); }
  .price-card.paid .price-card-accent { background: linear-gradient(90deg, var(--amber), #f7c97a); }

  .price-icon {
    width: 60px;
    height: 60px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    margin-bottom: 20px;
  }

  .price-card.free .price-icon { background: #f0fdf4; }
  .price-card.paid .price-icon { background: #fffbeb; }

  .price-card h2 {
    font-family: 'Playfair Display', serif;
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--ink);
    margin-bottom: 8px;
  }

  .price-tag {
    font-family: 'Playfair Display', serif;
    font-size: 2.4rem;
    font-weight: 800;
    margin-bottom: 14px;
  }

  .price-card.free .price-tag { color: #16a34a; }
  .price-card.paid .price-tag { color: var(--amber); }

  .price-card p {
    font-size: .92rem;
    color: #6b7a8d;
    line-height: 1.65;
    margin-bottom: 22px;
  }

  .price-features {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .price-features li {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: .87rem;
    color: var(--ink);
  }

  .check { color: #22c55e; font-size: 1rem; }
  .cross { color: var(--red-accent); font-size: 1rem; }

  /* ── PROCESS ── */
  .process-section {
    background: var(--ink);
    border-radius: 24px;
    padding: 60px;
    margin-bottom: 70px;
    position: relative;
    overflow: hidden;
  }

  .process-section::before {
    content: '📚';
    position: absolute;
    right: 50px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 200px;
    opacity: .04;
    pointer-events: none;
  }

  .process-section .section-label { color: var(--amber-light); }
  .process-section .section-label::before { background: var(--amber-light); }
  .process-section .section-title { color: #fff; margin-bottom: 50px; }

  .steps-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0;
    position: relative;
  }

  .steps-grid::before {
    content: '';
    position: absolute;
    top: 30px;
    left: 10%;
    right: 10%;
    height: 2px;
    background: linear-gradient(90deg, var(--sage), var(--amber));
    opacity: .4;
    z-index: 0;
  }

  .step-item {
    text-align: center;
    position: relative;
    z-index: 1;
    padding: 0 20px;
  }

  .step-num {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    margin: 0 auto 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.3rem;
    font-weight: 700;
    font-family: 'Playfair Display', serif;
  }

  .step-item:nth-child(1) .step-num { background: var(--sage); color: #fff; }
  .step-item:nth-child(2) .step-num { background: var(--amber); color: var(--ink); }
  .step-item:nth-child(3) .step-num { background: #7b5ea7; color: #fff; }
  .step-item:nth-child(4) .step-num { background: var(--red-accent); color: #fff; }

  .step-emoji {
    font-size: 1.6rem;
    display: block;
    margin-bottom: 10px;
  }

  .step-title {
    font-size: 1rem;
    font-weight: 700;
    color: #fff;
    margin-bottom: 8px;
  }

  .step-desc {
    font-size: .8rem;
    color: var(--muted);
    line-height: 1.5;
  }

  /* ── NOTICE ── */
  .notice-section {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
  }

  .notice-card {
    background: #fff;
    border-radius: 20px;
    padding: 36px;
    box-shadow: 0 4px 24px rgba(0,0,0,.05);
  }

  .notice-card h3 {
    font-family: 'Playfair Display', serif;
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--ink);
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .notice-list {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .notice-list li {
    display: flex;
    gap: 12px;
    font-size: .9rem;
    color: #4a5568;
    line-height: 1.5;
  }

  .notice-list li::before {
    content: '→';
    color: var(--amber);
    font-weight: 700;
    flex-shrink: 0;
    margin-top: 1px;
  }

  .highlight-box {
    background: linear-gradient(135deg, var(--sage), var(--sage-light));
    border-radius: 20px;
    padding: 36px;
    color: #fff;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .highlight-box h3 {
    font-family: 'Playfair Display', serif;
    font-size: 1.4rem;
    font-weight: 700;
    margin-bottom: 12px;
  }

  .highlight-box p {
    opacity: .85;
    font-size: .9rem;
    line-height: 1.6;
    margin-bottom: 28px;
  }

  .highlight-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(255,255,255,.2);
    border: 1.5px solid rgba(255,255,255,.4);
    color: #fff;
    padding: 12px 24px;
    border-radius: 10px;
    font-weight: 600;
    text-decoration: none;
    font-size: .9rem;
    transition: background .2s;
    align-self: flex-start;
  }

  .highlight-link:hover { background: rgba(255,255,255,.3); }

  /* ── FOOTER NOTE ── */
  .footer-note {
    text-align: center;
    padding: 40px 60px;
    color: var(--muted);
    font-size: .83rem;
    border-top: 1px solid #e8e8e8;
  }

  /* ── RESPONSIVE ── */
  @media (max-width: 960px) {
    .hero-inner {
      grid-template-columns: 1fr;
      padding: 60px 30px 90px;
      text-align: center;
    }
    .hero-headline { font-size: 2.6rem; }
    .hero-sub { margin: 0 auto 40px; }
    .hero-ctas { justify-content: center; }
    .card-stage { display: flex; justify-content: center; }
    .id-card { transform: none; width: 300px; }
    .stats-inner { grid-template-columns: repeat(2, 1fr); gap: 20px; }
    .stat-item { border-right: none; }
    .main-content { padding: 50px 30px; }
    .pricing-grid, .notice-section { grid-template-columns: 1fr; }
    .steps-grid { grid-template-columns: repeat(2, 1fr); gap: 30px; }
    .steps-grid::before { display: none; }
    .process-section { padding: 40px 30px; }
    .stats-bar { padding: 28px 30px; }
  }
`;

const barcodeBars = [3,1,2,1,3,2,1,2,3,1,2,3,1,2,1,3,2,1,2,3];

export default function IDCardInfo() {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const target = 12400;
    const step = Math.ceil(target / 60);
    let c = 0;
    const t = setInterval(() => {
      c = Math.min(c + step, target);
      setCount(c);
      if (c >= target) clearInterval(t);
    }, 25);
    return () => clearInterval(t);
  }, []);

  return (
    <>
      <style>{styles}</style>
      <div className="id-page">

        {/* HERO */}
        <section className="hero">
          <div className="hero-bg-lines" />
          <div className="hero-inner">
            <div className="hero-text">
              <div className="eyebrow">
                <span className="eyebrow-dot" />
                University Library · Jahangirnagar University
              </div>
              <h1 className="hero-headline">
                Your <em>Library ID Card</em>{" "}
                Starts Here
              </h1>
              <p className="hero-sub">
                Apply online, track your application status, and unlock full access to one of the largest university library systems in Bangladesh — all in a few easy steps.
              </p>
              <div className="hero-ctas">
                <a href="https://devpis.juniv.edu/" className="cta-primary">
                  Apply for ID Card →
                </a>
                <a href="https://devpis.juniv.edu/" className="cta-secondary">
                  Track My Application
                </a>
              </div>
            </div>

            {/* ID Card Mockup */}
            <div className="card-stage">
              <div className="id-card">
                <div className="card-top-stripe" />
                <div className="card-header-bar">
                  <div className="card-uni-name">Jahangirnagar University</div>
                  <div className="card-logo-circle">📚</div>
                </div>
                <div className="card-body">
                  <div className="card-photo">👨‍🎓</div>
                  <div className="card-details">
                    <div className="card-label">Full Name</div>
                    <div className="card-value">Mohammad Rahman</div>
                    <div className="card-label">Student ID</div>
                    <div className="card-value">JU-2024-0342</div>
                    <div className="card-label">Department</div>
                    <div className="card-value">Computer Science and Engineering</div>
                    <div className="card-type-badge">Student</div>
                  </div>
                </div>
                <div className="card-footer-bar">
                  <div className="card-barcode">
                    {barcodeBars.map((w, i) => (
                      <span key={i} style={{ width: `${w * 2}px` }} />
                    ))}
                  </div>
                  <div className="card-valid">Valid: 2024–2028</div>
                </div>
              </div>
            </div>
          </div>

          <div className="hero-angle">
            <svg viewBox="0 0 1440 70" preserveAspectRatio="none">
              <path d="M0,0 L1440,50 L1440,70 L0,70 Z" fill="#1a2e45" />
            </svg>
          </div>
        </section>

        {/* STATS BAR 
        <div className="stats-bar">
          <div className="stats-inner">
            <div className="stat-item">
              <div className="stat-num">{count.toLocaleString()}+</div>
              <div className="stat-desc">Active Members</div>
            </div>
            <div className="stat-item">
              <div className="stat-num">80,000+</div>
              <div className="stat-desc">Books & Journals</div>
            </div>
            <div className="stat-item">
              <div className="stat-num">2–5</div>
              <div className="stat-desc">Days Processing</div>
            </div>
            <div className="stat-item">
              <div className="stat-num">100%</div>
              <div className="stat-desc">Free for 1st Years</div>
            </div>
          </div>
        </div>
*/}
        {/* MAIN CONTENT */}
        <div className="main-content">

          {/* PRICING */}
          <div className="section-label">Card Eligibility</div>
          <h2 className="section-title">Who Pays, Who Doesn't</h2>

          <div className="pricing-grid">
            <div className="price-card free">
              <div className="price-card-accent" />
              <div className="price-icon">🎓</div>
              <h2>1st Year Students</h2>
              <div className="price-tag">FREE</div>
              <p>
                Newly admitted students receive their first library ID card at no cost. It's our welcome gift to you as you begin your academic journey at JU.
              </p>
              <ul className="price-features">
                <li><span className="check">✓</span> No payment required</li>
                <li><span className="check">✓</span> Full library access included</li>
                <li><span className="check">✓</span> Digital + physical borrowing</li>
                <li><span className="cross">✗</span> Replacement cards are charged</li>
              </ul>
            </div>

            <div className="price-card paid">
              <div className="price-card-accent" />
              <div className="price-icon">💳</div>
              <h2>2nd Year & Beyond</h2>
              <div className="price-tag">Fee Applies</div>
              <p>
                 Teachers, Officers, Staffs and students from second year onwards are required to pay the prescribed library card fee. Fees also apply for lost or damaged card replacements.
              </p>
              <ul className="price-features">
                <li><span className="check">✓</span> Paid via campus finance office</li>
                <li><span className="check">✓</span> Same full access privileges</li>
                <li><span className="check">✓</span> Receipt required at collection</li>
                <li><span className="cross">✗</span> No waiver for lost cards</li>
              </ul>
            </div>
          </div>

          {/* PROCESS */}
          <div className="process-section">
            <div className="section-label">How It Works</div>
            <h2 className="section-title">Four Steps to Your Card</h2>
            <div className="steps-grid">
              {[
                { n: "01", emoji: "📝", title: "Submit Application", desc: "Fill in the online form with your student details and department information." },
                { n: "02", emoji: "🔍", title: "Verification", desc: "Library staff verify your enrollment and student ID with the registrar's office." },
                { n: "03", emoji: "🖨️", title: "Card Printing", desc: "Your personalised ID card is printed and quality-checked by library staff." },
                { n: "04", emoji: "🪪", title: "Card Collection", desc: "Visit the library counter with your student ID to collect your card in person." },
              ].map((s, i) => (
                <div className="step-item" key={i}>
                  <div className="step-num">{s.n}</div>
                  <span className="step-emoji">{s.emoji}</span>
                  <div className="step-title">{s.title}</div>
                  <p className="step-desc">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* NOTICE */}
          <div className="section-label">Good to Know</div>
          <h2 className="section-title">Important Policies</h2>

          <div className="notice-section">
            <div className="notice-card">
              <h3>📋 General Rules</h3>
              <ul className="notice-list">
                <li>First-issue cards for 1st year students are completely free of charge.</li>
                <li>Lost or damaged cards require an official reissue with applicable fee payment.</li>
                <li>Processing typically takes 2–5 working days after successful verification.</li>
                <li>You must bring your original student ID when collecting the card.</li>
                <li>Cards are non-transferable and strictly for personal use only.</li>
              </ul>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
              <div className="notice-card">
                <h3>🕐 Library Hours</h3>
                <ul className="notice-list">
                  <li>Weekdays: 7:30 AM – 9:30 PM</li>
                  <li>Friday: 2:30 PM – 9:00 PM (post-Jumu'ah)</li>
                  <li>Saturdays: 9:00 AM – 5:00 PM</li>
                  <li>Closed on public and university holidays</li>
                </ul>
              </div>
              <div className="highlight-box">
                <div>
                  <h3>Need Help? 📬</h3>
                  <p>Our library helpdesk is available on working days to assist with applications, queries, and card-related issues.</p>
                </div>
                <a href="mailto:library@juniv.edu" className="highlight-link">
                  library@juniv.edu →
                </a>
              </div>
            </div>
          </div>

        </div>

        <div className="footer-note">
          Jahangirnagar University Central Library · ID Card Services · For assistance, visit the main library counter during opening hours.
        </div>

      </div>
    </>
  );
}